import type { StimulationService } from '../stimulation/StimulationService.ts';
import { button, el } from './dom.ts';

export interface SafetyBarCallbacks {
  onArm: () => void;
  onDisarm: () => void;
  onEmergencyStop: () => void;
  onClearStop: () => void;
}

/**
 * The persistent stimulation status banner and emergency stop.
 *
 * Always visible, always at the top, and coloured so that the state is readable
 * from across a room:
 *
 *   grey   SIMULATION            nothing can reach a person
 *   blue   CONNECTED / DISARMED  hardware is attached and inert
 *   red    CONNECTED / ARMED     stimulation can happen at any moment
 *   black  EMERGENCY STOP        latched off until an operator clears it
 *
 * The arm control is deliberately a plain button that must be pressed, not a
 * mode that is entered as a side effect of choosing an experiment preset.
 * Selecting "HHI Human" from a dropdown arms nothing.
 *
 * The software stop here disarms the stimulator, stops new requests, and pauses
 * autonomous control. It is a convenience. The mechanism actually relied upon
 * is the hardware's own: the intensity dial, the power switch, and the
 * participant's ability to remove the electrodes.
 */
export class SafetyBar {
  readonly root: HTMLElement;

  private statusEl: HTMLElement;
  private detailEl: HTMLElement;
  private armBtn: HTMLButtonElement;
  private disarmBtn: HTMLButtonElement;
  private stopBtn: HTMLButtonElement;
  private clearBtn: HTMLButtonElement;

  constructor(
    private readonly service: StimulationService,
    callbacks: SafetyBarCallbacks,
  ) {
    this.root = el('div', 'safety-bar');

    const left = el('div', 'safety-left');
    this.statusEl = el('div', 'safety-status', 'SIMULATION');
    this.detailEl = el('div', 'safety-detail', 'Mock stimulator — no physical output');
    left.append(this.statusEl, this.detailEl);

    const right = el('div', 'safety-actions');
    this.armBtn = button('START GAME', callbacks.onArm, 'btn-arm');
    this.disarmBtn = button('DISARM', callbacks.onDisarm, 'btn-disarm');
    this.clearBtn = button('Clear stop', callbacks.onClearStop, 'btn-clear');
    this.stopBtn = button('■ STOP', callbacks.onEmergencyStop, 'btn-stop');
    this.stopBtn.title = 'Disarm, stop new stimulation requests, and pause autonomous control (Esc)';
    right.append(this.armBtn, this.disarmBtn, this.clearBtn, this.stopBtn);

    this.root.append(left, right);
  }

  update(): void {
    const s = this.service.getStatus();
    const gate = this.service.gate.getState();

    let cls: string;
    let text: string;
    let detail: string;

    if (gate.emergencyStopped) {
      cls = 'stopped';
      text = 'EMERGENCY STOP';
      detail = 'Latched. Clear the stop, then re-arm, before stimulation is possible again.';
    } else if (!s.connected) {
      cls = s.simulated ? 'simulation' : 'disconnected';
      text = s.simulated ? 'SIMULATION' : 'DISCONNECTED';
      detail = `${s.label} — not connected`;
    } else if (!s.armed) {
      cls = s.simulated ? 'simulation' : 'disarmed';
      text = s.simulated ? 'SIMULATION / DISARMED' : 'CONNECTED / DISARMED';
      detail = `${s.label} — ready to start`;
    } else {
      cls = s.simulated ? 'simulation-armed' : 'armed';
      text = s.simulated ? 'SIMULATION / ARMED' : 'CONNECTED / ARMED';
      detail = s.simulated
        ? `${s.label} — armed (no physical output)`
        : `${s.label} — game running · Escape stops immediately`;
    }

    detail += `  ·  ${gate.sessionStimuli} stimuli this session · ${gate.inLastSecond}/s`;

    this.root.className = `safety-bar state-${cls}`;
    if (this.statusEl.textContent !== text) this.statusEl.textContent = text;
    this.detailEl.textContent = detail;

    this.armBtn.disabled = !s.connected || s.armed || gate.emergencyStopped;
    this.disarmBtn.disabled = !s.armed;
    this.clearBtn.disabled = !gate.emergencyStopped;
    this.clearBtn.hidden = !gate.emergencyStopped;
  }
}
